import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { GitCommit, RefreshCw, Search, Calendar, User, Code2, DollarSign, CheckCircle, AlertCircle, ExternalLink, Grid3X3, Navigation, Shield, Clock, Receipt, Wallet, LockKeyhole, FileText, PencilLine, DownloadCloud, History, FolderTree, Eye, Download, Trash2, Flag, Settings } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { addDays, format, startOfDay, startOfWeek, subWeeks } from 'date-fns';
import {
  Timestamp,
  collection,
  deleteField,
  doc,
  getDocFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type { QueryConstraint } from 'firebase/firestore';
import { isAdminAuthenticated } from '@/lib/adminAuth';
import { isBetaAdminAuthenticated } from '@/lib/betaAuth';
import { BetaEnableCard, BetaDashboardVisibilityCard, ComingSoonToggleCard } from '@/features/developer/BetaAccessToggleCard';

import { Link, useLocation } from 'react-router-dom';

import { CodeSnapshotCard } from '@/pages/developer/CodeSnapshotCard';
import { DeveloperControlsDialog } from '@/pages/developer/DeveloperControlsDialog';
import { DeveloperOverviewHeader } from '@/pages/developer/DeveloperOverviewHeader';
import { DevicePreviewControls, DevicePreviewFrame, type DevicePreviewId, type DevicePreviewKind } from '@/pages/developer/DevicePreview';
import { MobileDeveloperBottomNav, MobileDeveloperToolsBar, type MobileDeveloperSectionId } from '@/pages/developer/MobileDeveloperNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { InvoiceEditorDialog } from '@/pages/developer/InvoiceEditorDialog';
import { InvoicesCard } from '@/pages/developer/InvoicesCard';
import { FinancialSnapshotCard } from '@/pages/developer/FinancialSnapshotCard';
import { BillingCard } from '@/components/developer/BillingCard';
import { ICSStatusCard } from '@/components/developer/ICSStatusCard';
import { WorkorderCommitsDialog } from '@/pages/developer/WorkorderCommitsDialog';
import { AssignCommitToWorkorderDialog } from '@/pages/developer/AssignCommitToWorkorderDialog';
import { CommitHistoryCard } from '@/pages/developer/CommitHistoryCard';
import type { CommitAssignmentFilter } from '@/pages/developer/CommitHistoryCard';
import { DeveloperPageDialogs } from '@/pages/developer/DeveloperPageDialogs';
import { PublishedBuildsCard } from '@/pages/developer/PublishedBuildsCard';
import { TimeExpenseTrackingCard } from '@/pages/developer/TimeExpenseTrackingCard';
import { PageController } from '@/pages/developer/PageController';
import { useCommitHistoryFilters } from '@/features/developer/useCommitHistoryFilters';
import { useFirestoreCommitPaging } from '@/features/developer/useFirestoreCommitPaging';
import { useDeveloperGitHubSync } from '@/features/developer/useDeveloperGitHubSync';
import { useDeveloperRepoSnapshot } from '@/features/developer/useDeveloperRepoSnapshot';
import { useDeveloperWorkorders } from '@/features/developer/useDeveloperWorkorders';
import { useDeveloperFinanceActions } from '@/features/developer/useDeveloperFinanceActions';
import {
  COMMIT_ACTIVITY_WEEKS_DESKTOP,
  COMMIT_ACTIVITY_WEEKS_MOBILE,
  COMMIT_ACTIVITY_WEEKS_TABLET,
  DEVELOPER_PASSCODE,
  FIRESTORE_COLLECTION,
  FIRESTORE_COMMITS_PAGE_SIZE,
  FIRESTORE_COMMITS_PREVIEW_LIMIT,
  FIRESTORE_COMMITS_QUERY_LIMIT,
  FIRESTORE_DOC_ID,
  RATE_PER_COMMIT,
  RETAINER_PAID,
} from '@/features/developer/developerConstants';
import {
  getOrCreateActorId,
  generateId,
  getStoredDeveloperUnlock,
  getStoredRatePerCommit,
  getStoredRatePerHour,
  getStoredRepoName,
  getStoredRepoOwner,
  setStoredDeveloperUnlock,
  setStoredRatePerCommit,
  setStoredRatePerHour,
  setStoredRepoName,
  setStoredRepoOwner,
} from '@/pages/developer/storage';
import { isPullRequestCommit, normalizeBillingAmount, parseGitHubRepoIdentity, sleep } from '@/pages/developer/githubUtils';
import type {
  CodeDownloadLog,
  Commit,
  Customer,
  DeveloperSnapshot,
  Expense,
  HostingDeployment,
  InvoiceDraft,
  JournalEntry,
  Milestone,
  RepoFile,
  TimeEntry,
} from '@/pages/developer/types';
import { INTERNAL_CUSTOMER_ID, INTERNAL_CUSTOMER_NAME, formatJournalEntryType } from '@/pages/developer/types';
import { buildZipBlob } from '@/features/developer/zipUtils';
import { db, isFirebaseConfigured } from '@/lib/firebase';

const BUILD_GIT_SHA = (import.meta.env.VITE_GIT_SHA ?? '').trim();
const BUILD_TIME_UTC = (import.meta.env.VITE_BUILD_TIME ?? '').trim();

const getFirestoreOverviewDocRef = () => {
  return db ? doc(db, FIRESTORE_COLLECTION, FIRESTORE_DOC_ID) : null;
};
const getFirestoreOverviewCommitsCollectionRef = () => {
  const docRef = getFirestoreOverviewDocRef();
  return docRef ? collection(docRef, 'commits') : null;
};
const getFirestoreOverviewCodeDownloadsCollectionRef = () => {
  const docRef = getFirestoreOverviewDocRef();
  return docRef ? collection(docRef, 'codeDownloads') : null;
};
const getFirestoreOverviewHostingDeploymentsCollectionRef = () => {
  const docRef = getFirestoreOverviewDocRef();
  return docRef ? collection(docRef, 'hostingDeployments') : null;
};
const getFirestoreOverviewMilestonesCollectionRef = () => {
  const docRef = getFirestoreOverviewDocRef();
  return docRef ? collection(docRef, 'milestones') : null;
};
// NOTE: The client does not handle GitHub tokens; private access goes through the server proxy.

const CUSTOMER_CREATE_SENTINEL = '__create_customer__';


// buildZipBlob moved to src/features/developer/zipUtils.ts

export default function Developer() {
  const isMobile = useIsMobile();
  const [isCompactView, setIsCompactView] = useState(false);

  const isPreviewEmbedded = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      return new URLSearchParams(window.location.search).get('embedded') === '1';
    } catch {
      return false;
    }
  }, []);

  const previewDevice = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      return new URLSearchParams(window.location.search).get('previewDevice');
    } catch {
      return null;
    }
  }, []);

  const previewSafeAreaBottom = useMemo(() => {
    if (!isPreviewEmbedded) return 0;
    // Best-effort approximation for iOS home-indicator devices.
    if (previewDevice === 'iphone-15' || previewDevice === 'iphone-15-pro-max') return 34;
    return 0;
  }, [isPreviewEmbedded, previewDevice]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('preview-embedded', isPreviewEmbedded);
    return () => {
      document.body.classList.remove('preview-embedded');
    };
  }, [isPreviewEmbedded]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const addMqlListener = (list: MediaQueryList, listener: () => void) => {
      if (typeof list.addEventListener === 'function') list.addEventListener('change', listener);
      else list.addListener(listener);
    };

    const removeMqlListener = (list: MediaQueryList, listener: () => void) => {
      if (typeof list.removeEventListener === 'function') list.removeEventListener('change', listener);
      else list.removeListener(listener);
    };

    const resolveCompactMaxWidth = () => {
      // Compact mode is the phone-only layout (bottom tabs, single-section view).
      // Tablets use the full (desktop-style) layout.
      return 767;
    };

    let mql = window.matchMedia(`(max-width: ${resolveCompactMaxWidth()}px)`);

    const update = () => {
      const nextMaxWidth = resolveCompactMaxWidth();
      const nextQuery = `(max-width: ${nextMaxWidth}px)`;
      if (mql.media !== nextQuery) {
        removeMqlListener(mql, update);
        mql = window.matchMedia(nextQuery);
        addMqlListener(mql, update);
      }
      setIsCompactView(mql.matches);
    };
    update();
    addMqlListener(mql, update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      removeMqlListener(mql, update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [isPreviewEmbedded, previewDevice]);

  const [mobileSection, setMobileSection] = useState<MobileDeveloperSectionId>(() => {
    if (typeof window === 'undefined') return 'overview';
    try {
      const stored = window.localStorage.getItem('developerMobileSection');
      if (!stored) return 'overview';
      return stored as MobileDeveloperSectionId;
    } catch {
      return 'overview';
    }
  });

  useEffect(() => {
    if (!isCompactView) return;
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('developerMobileSection', mobileSection);
    } catch {
      // ignore
    }
  }, [isCompactView, mobileSection]);

  const [devicePreview, setDevicePreview] = useState<DevicePreviewId>(() => {
    if (typeof window === 'undefined') return 'full';
    try {
      const stored = window.localStorage.getItem('developerDevicePreview');
      if (!stored) return 'full';
      return stored as DevicePreviewId;
    } catch {
      return 'full';
    }
  });

  const getKindForDevice = useCallback((id: DevicePreviewId): DevicePreviewKind => {
    if (id === 'full') return 'desktop';
    if (id.startsWith('ipad-')) return 'tablet';
    return 'phone';
  }, []);

  const [devicePreviewKind, setDevicePreviewKind] = useState<DevicePreviewKind>(() => {
    if (typeof window === 'undefined') return getKindForDevice('full');
    try {
      const stored = window.localStorage.getItem('developerDevicePreviewKind');
      if (stored === 'desktop' || stored === 'phone' || stored === 'tablet') return stored;
    } catch {
      // ignore
    }
    return getKindForDevice(devicePreview);
  });

  const addMqlListener = useCallback((list: MediaQueryList, listener: () => void) => {
    if (typeof list.addEventListener === 'function') list.addEventListener('change', listener);
    else list.addListener(listener);
  }, []);

  const removeMqlListener = useCallback((list: MediaQueryList, listener: () => void) => {
    if (typeof list.removeEventListener === 'function') list.removeEventListener('change', listener);
    else list.removeListener(listener);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('developerDevicePreview', devicePreview);
    } catch {
      // ignore
    }
  }, [devicePreview]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('developerDevicePreviewKind', devicePreviewKind);
    } catch {
      // ignore
    }
  }, [devicePreviewKind]);

  useEffect(() => {
    const inferred = getKindForDevice(devicePreview);
    if (inferred !== devicePreviewKind) {
      setDevicePreviewKind(inferred);
    }
  }, [devicePreview, devicePreviewKind, getKindForDevice]);

  const [actorId] = useState(getOrCreateActorId);
  const [commits, setCommits] = useState<Commit[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    searchQuery,
    setSearchQuery,
    commitFilter,
    setCommitFilter,
    filteredCommits,
    filteredCommitShas,
  } = useCommitHistoryFilters(commits);
  const [commitBillingSha, setCommitBillingSha] = useState<string | null>(null);
  const [commitsPerPage, setCommitsPerPage] = useState<10 | 25 | 50 | 100>(10);
  const [commitPage, setCommitPage] = useState(1);
  const [commitAssignmentFilter, setCommitAssignmentFilter] = useState<CommitAssignmentFilter>('unassigned');
  const [ratePerCommit, setRatePerCommit] = useState(getStoredRatePerCommit);
  const [retainerPaid, setRetainerPaid] = useState(RETAINER_PAID);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [timeDate, setTimeDate] = useState('');
  const [timeHours, setTimeHours] = useState('');
  const [timeHourlyRate, setTimeHourlyRate] = useState(() => getStoredRatePerHour().toFixed(2));
  const [timeDescription, setTimeDescription] = useState('');
  const [gitOrderDescription, setGitOrderDescription] = useState('');
  const [timeMisc, setTimeMisc] = useState('');
  const [timeTaxes, setTimeTaxes] = useState('');
  const [timeEntryCommitShas, setTimeEntryCommitShas] = useState<string[]>([]);
  const [timeEntryMarkCommitsComplete, setTimeEntryMarkCommitsComplete] = useState(true);
  const [expenseDate, setExpenseDate] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseMisc, setExpenseMisc] = useState('');
  const [expenseTaxes, setExpenseTaxes] = useState('');
  const [expenseNotes, setExpenseNotes] = useState('');
  const [expenseJournalEntry, setExpenseJournalEntry] = useState('');
  const [expenseAttachment, setExpenseAttachment] = useState<File | null>(null);
  const [rateDraft, setRateDraft] = useState(() => String(getStoredRatePerCommit()));
  const [hourlyRateDraft, setHourlyRateDraft] = useState(() => getStoredRatePerHour().toFixed(2));
  const [journalDate, setJournalDate] = useState('');
  const [journalType, setJournalType] = useState<JournalEntry['entryType']>('retainer');
  const [journalCustomerId, setJournalCustomerId] = useState<string>(INTERNAL_CUSTOMER_ID);
  const [journalAmount, setJournalAmount] = useState('');
  const [journalMisc, setJournalMisc] = useState('');
  const [journalTaxes, setJournalTaxes] = useState('');
  const [journalNotes, setJournalNotes] = useState('');
  const [invoiceEditorOpen, setInvoiceEditorOpen] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState<InvoiceDraft | null>(null);
  const [pendingInvoiceEditId, setPendingInvoiceEditId] = useState<string | null>(null);

  const [workorderDialogOpen, setWorkorderDialogOpen] = useState(false);
  const [workorderDialogEntryId, setWorkorderDialogEntryId] = useState<string | null>(null);
  const [workorderDialogCommitShas, setWorkorderDialogCommitShas] = useState<string[]>([]);
  const [workorderDialogRate, setWorkorderDialogRate] = useState('');
  const [workorderDialogHours, setWorkorderDialogHours] = useState('');
  const [workorderDialogHourlyRate, setWorkorderDialogHourlyRate] = useState(() => getStoredRatePerHour().toFixed(2));
  const [workorderDialogMisc, setWorkorderDialogMisc] = useState('');
  const [workorderDialogTaxes, setWorkorderDialogTaxes] = useState('');
  const [workorderDialogSaving, setWorkorderDialogSaving] = useState(false);

  const [assignCommitDialogOpen, setAssignCommitDialogOpen] = useState(false);
  const [assignCommitDialogShas, setAssignCommitDialogShas] = useState<string[]>([]);
  const [assignCommitDialogWorkorderId, setAssignCommitDialogWorkorderId] = useState('');
  const [assignCommitDialogSaving, setAssignCommitDialogSaving] = useState(false);
  const [assignCommitDialogNewWorkorderDescription, setAssignCommitDialogNewWorkorderDescription] = useState('');
  const [assignCommitDialogCreating, setAssignCommitDialogCreating] = useState(false);
  const [developerDialogOpen, setDeveloperDialogOpen] = useState(false);
  const [developerPasscode, setDeveloperPasscode] = useState('');
  const [developerUnlocked, setDeveloperUnlocked] = useState(() => getStoredDeveloperUnlock());
  const [dataSource, setDataSource] = useState<'firestore' | 'local' | 'github'>('local');
  const [repoOwner, setRepoOwner] = useState(() => getStoredRepoOwner());
  const [repoName, setRepoName] = useState(() => getStoredRepoName());
  const [lastCommitSyncAt, setLastCommitSyncAt] = useState<string | null>(null);
  const [firestoreCommitCount, setFirestoreCommitCount] = useState<number | null>(null);
  const [firestoreTotalAdditions, setFirestoreTotalAdditions] = useState<number | null>(null);
  const [firestoreTotalDeletions, setFirestoreTotalDeletions] = useState<number | null>(null);
  const [firestoreTotalStatsCommitCount, setFirestoreTotalStatsCommitCount] = useState<number | null>(null);
  const [firestoreTotalsUpdatedAt, setFirestoreTotalsUpdatedAt] = useState<string | null>(null);
  const [firestoreCommitsUpdatedAt, setFirestoreCommitsUpdatedAt] = useState<string | null>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [reloadDialogOpen, setReloadDialogOpen] = useState(false);
  const [repoFiles, setRepoFiles] = useState<RepoFile[]>([]);
  const [repoFilesUpdatedAt, setRepoFilesUpdatedAt] = useState<string | null>(null);
  const [repoFilesStorage, setRepoFilesStorage] = useState<'doc' | 'subcollection'>('doc');
  const [repoFileCount, setRepoFileCount] = useState<number | null>(null);
  const [repoFilesHydratedAt, setRepoFilesHydratedAt] = useState<string | null>(null);
  const [repoFilesLoading, setRepoFilesLoading] = useState(false);
  const [repoFilesError, setRepoFilesError] = useState<string | null>(null);
  const [selectedRepoFile, setSelectedRepoFile] = useState<RepoFile | null>(null);
  const [repoSnapshotCommitSha, setRepoSnapshotCommitSha] = useState<string | null>(null);
  const [repoSnapshotVersion, setRepoSnapshotVersion] = useState<string | null>(null);
  const [repoSnapshotGeneratingCommitSha, setRepoSnapshotGeneratingCommitSha] = useState<string | null>(null);
  const [downloadReasonDialogOpen, setDownloadReasonDialogOpen] = useState(false);
  const [downloadReason, setDownloadReason] = useState('');
  const [codeDownloadLogs, setCodeDownloadLogs] = useState<CodeDownloadLog[]>([]);
  const [codeDownloadLogsLoading, setCodeDownloadLogsLoading] = useState(false);
  const [codeDownloadLogsError, setCodeDownloadLogsError] = useState<string | null>(null);

  const [hostingDeployments, setHostingDeployments] = useState<HostingDeployment[]>([]);
  const [hostingDeploymentsLoading, setHostingDeploymentsLoading] = useState(false);
  const [hostingDeploymentsError, setHostingDeploymentsError] = useState<string | null>(null);
  
  const [deleteSnapshotDialogOpen, setDeleteSnapshotDialogOpen] = useState(false);
  const [deleteSnapshotLoading, setDeleteSnapshotLoading] = useState(false);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [milestonesError, setMilestonesError] = useState<string | null>(null);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [milestoneTitle, setMilestoneTitle] = useState('');
  const [milestoneSaving, setMilestoneSaving] = useState(false);
  const [milestoneTargetCommitSha, setMilestoneTargetCommitSha] = useState<string | null>(null);
  const [commitActivityWeeks, setCommitActivityWeeks] = useState(COMMIT_ACTIVITY_WEEKS_DESKTOP);
  const [commitActivityLabel, setCommitActivityLabel] = useState('Last 12 months');
  const rateDraftDirtyRef = useRef(false);
  const hourlyRateDraftDirtyRef = useRef(false);
  const rateOverrideRef = useRef(false);
  const repoOwnerDirtyRef = useRef(false);
  const repoNameDirtyRef = useRef(false);
  const firestoreCommitsHydratedAtRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const update = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setCommitActivityWeeks(COMMIT_ACTIVITY_WEEKS_MOBILE);
        setCommitActivityLabel('Last 3 months');
      } else if (width < 1024) {
        setCommitActivityWeeks(COMMIT_ACTIVITY_WEEKS_TABLET);
        setCommitActivityLabel('Last 6 months');
      } else {
        setCommitActivityWeeks(COMMIT_ACTIVITY_WEEKS_DESKTOP);
        setCommitActivityLabel('Last 12 months');
      }
    };

    const mobileMql = window.matchMedia('(max-width: 767px)');
    const tabletMql = window.matchMedia('(max-width: 1023px)');

    addMqlListener(mobileMql, update);
    addMqlListener(tabletMql, update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    update();

    return () => {
      removeMqlListener(mobileMql, update);
      removeMqlListener(tabletMql, update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const firestoreDocRef = getFirestoreOverviewDocRef();
  const firestoreCommitsCollectionRef = getFirestoreOverviewCommitsCollectionRef();
  const firestoreCodeDownloadsCollectionRef = getFirestoreOverviewCodeDownloadsCollectionRef();
  const firestoreDeploymentsCollectionRef = getFirestoreOverviewHostingDeploymentsCollectionRef();
  const firestoreMilestonesCollectionRef = getFirestoreOverviewMilestonesCollectionRef();
  const canViewCommitData = isAdminUser;
  const canManageDeveloperSettings = isAdminUser;
  const canViewRepoSnapshot = isAdminUser;

  const {
    firestoreCommitsLoadedCount,
    firestoreCommitsPagingLoading,
    firestoreCommitsPagingError,
    canLoadMoreFirestoreCommits,
    loadMoreFirestoreCommits,
    loadInitialFirestoreCommits,
    loadCommitsFromFirestore,
  } = useFirestoreCommitPaging({
    firestoreCommitsCollectionRef,
    ratePerCommit,
    dataSource,
    commitsLength: commits.length,
    firestoreCommitCount,
    pageSize: FIRESTORE_COMMITS_PAGE_SIZE,
    queryLimit: FIRESTORE_COMMITS_QUERY_LIMIT,
    setCommits,
    setDataSource,
  });

  const {
    gitHubProxyPingLoading,
    lastGitHubSyncError,
    loadingAllCommits,
    loadingNewCommits,
    recordGitHubSyncError,
    handlePingGitHubProxy,
    handleCheckGitHubAuth,
    fetchAllCommitsFromGitHub,
    fetchNewCommitsFromGitHub,
  } = useDeveloperGitHubSync({
    repoOwner,
    repoName,
    ratePerCommit,
    commits,
    timeEntries,
    expenses,
    retainerPaid,
    journalEntries,
    lastCommitSyncAt,
    firestoreDocRef,
    firestoreCommitsCollectionRef,
    loadCommitsFromFirestore,
    setCommits,
    setDataSource,
    setLastCommitSyncAt,
    setFirestoreCommitCount,
    setFirestoreTotalAdditions,
    setFirestoreTotalDeletions,
    setFirestoreTotalStatsCommitCount,
    setFirestoreTotalsUpdatedAt,
  });

  const availableMobileSections = useMemo<MobileDeveloperSectionId[]>(() => {
    const next: MobileDeveloperSectionId[] = ['overview', 'invoices'];
    if (canViewCommitData) next.push('commits');
    if (canViewRepoSnapshot) next.push('releases');
    if (isAdminUser) next.push('time');
    return next;
  }, [canViewCommitData, canViewRepoSnapshot, isAdminUser]);

  useEffect(() => {
    if (!isCompactView) return;
    if (!availableMobileSections.includes(mobileSection)) {
      setMobileSection(availableMobileSections[0] ?? 'overview');
    }
  }, [availableMobileSections, isCompactView, mobileSection]);

  const scrollToSection = useCallback((id: string) => {
    if (typeof document === 'undefined') return;
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const goToSection = useCallback(
    (section: MobileDeveloperSectionId, anchorId?: string) => {
      if (isCompactView) {
        setMobileSection(section);
        if (anchorId) {
          requestAnimationFrame(() => scrollToSection(anchorId));
        }
        return;
      }

      if (anchorId) scrollToSection(anchorId);
    },
    [isCompactView, scrollToSection]
  );

  const viewCommitInHistory = useCallback(
    (commitSha: string) => {
      const trimmed = (commitSha ?? '').trim();
      if (!trimmed) return;
      setSearchQuery(trimmed.slice(0, 7));
      setCommitPage(1);
      if (isCompactView) {
        setMobileSection('commits');
      }
      requestAnimationFrame(() => scrollToSection('commit-history'));
    },
    [isCompactView, scrollToSection, setCommitPage, setSearchQuery]
  );

  // commit filter persistence is handled by useCommitHistoryFilters.

  const repoSnapshotCurrentDocRef = useMemo(() => {
    if (!firestoreDocRef) return null;
    return doc(firestoreDocRef, 'repoSnapshots', 'current');
  }, [firestoreDocRef]);

  const repoSnapshotFilesCollectionRef = useMemo(() => {
    if (!repoSnapshotCurrentDocRef) return null;
    return collection(repoSnapshotCurrentDocRef, 'files');
  }, [repoSnapshotCurrentDocRef]);

  const {
    buildRepoSnapshotZipName,
    deleteCurrentSnapshotFilesFromFirestore,
    fetchRepoSnapshot,
    logCodeDownload,
  } = useDeveloperRepoSnapshot({
    actorId,
    canViewRepoSnapshot,
    commits,
    expenses,
    developerUnlocked,
    isAdminUser,
    journalEntries,
    ratePerCommit,
    repoOwner,
    repoName,
    repoFiles,
    repoFilesHydratedAt,
    repoFilesStorage,
    repoFilesUpdatedAt,
    firestoreDocRef,
    repoSnapshotCurrentDocRef,
    repoSnapshotFilesCollectionRef,
    firestoreCodeDownloadsCollectionRef,
    firestoreDeploymentsCollectionRef,
    timeEntries,
    retainerPaid,
    recordGitHubSyncError,
    setRepoFiles,
    setRepoFilesUpdatedAt,
    setRepoFilesStorage,
    setRepoFileCount,
    setRepoFilesHydratedAt,
    setRepoFilesLoading,
    setRepoFilesError,
    setSelectedRepoFile,
    setRepoSnapshotCommitSha,
    setRepoSnapshotVersion,
    setRepoSnapshotGeneratingCommitSha,
    setCodeDownloadLogs,
    setCodeDownloadLogsLoading,
    setCodeDownloadLogsError,
    setHostingDeployments,
    setHostingDeploymentsLoading,
    setHostingDeploymentsError,
  });

  useEffect(() => {
    if (!developerUnlocked && !isAdminUser) return;
    if (!firestoreMilestonesCollectionRef) return;

    setMilestonesLoading(true);
    setMilestonesError(null);

    const milestonesQuery = query(firestoreMilestonesCollectionRef, orderBy('createdAt', 'desc'), limit(100));
    const unsub = onSnapshot(
      milestonesQuery,
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as Partial<Omit<Milestone, 'createdAt'>> & {
            createdAt?: unknown;
            fromCommitSha?: string | null;
            toCommitSha?: string;
          };
          const createdAt =
            data.createdAt && typeof (data.createdAt as Timestamp).toDate === 'function'
              ? (data.createdAt as Timestamp).toDate().toISOString()
              : typeof data.createdAt === 'string'
                ? data.createdAt
                : new Date().toISOString();
          return {
            id: docSnap.id,
            title: typeof data.title === 'string' && data.title.trim() ? data.title.trim() : 'Milestone',
            createdAt,
            toCommitSha: typeof data.toCommitSha === 'string' ? data.toCommitSha : '',
            fromCommitSha: typeof data.fromCommitSha === 'string' ? data.fromCommitSha : null,
            commitCount: typeof data.commitCount === 'number' ? data.commitCount : 0,
            snapshotVersion: typeof data.snapshotVersion === 'string' ? data.snapshotVersion : null,
            state: data.state === 'closed' ? 'closed' : 'open',
            description: typeof data.description === 'string' ? data.description : null,
            dueOn: typeof data.dueOn === 'string' ? data.dueOn : null,
            github: {
              number: typeof data.github?.number === 'number' ? data.github.number : null,
              htmlUrl: typeof data.github?.htmlUrl === 'string' ? data.github.htmlUrl : null,
              apiUrl: typeof data.github?.apiUrl === 'string' ? data.github.apiUrl : null,
            },
          } satisfies Milestone;
        });
        setMilestones(next.filter((entry) => entry.toCommitSha));
        setMilestonesLoading(false);
      },
      (error) => {
        setMilestonesLoading(false);
        setMilestonesError(error instanceof Error ? error.message : 'Unable to load milestones.');
      }
    );

    return () => unsub();
  }, [developerUnlocked, firestoreMilestonesCollectionRef, isAdminUser]);

  const openMilestoneEditor = (commitSha: string) => {
    if (!developerUnlocked && !isAdminUser) {
      toast({ title: 'Developer passcode required', description: 'Unlock Developer Controls to add or edit milestones.' });
      setDeveloperDialogOpen(true);
      return;
    }

    const normalized = commitSha.trim();
    if (!normalized) return;

    const existing = milestones.find((milestone) => (milestone.toCommitSha ?? '').trim() === normalized) ?? null;
    setMilestoneTargetCommitSha(normalized);
    setMilestoneTitle(existing?.title ?? '');
    setMilestoneDialogOpen(true);
  };

  const closeGitOrder = useCallback(async (entryId: string) => {
    const entry = timeEntries.find((e) => e.id === entryId) ?? null;
    if (!entry) {
      toast({ title: 'Git order not found', description: 'That Git order could not be found.' });
      return;
    }

    // Compute totals
    const qty = (entry.commitShas ?? []).length;
    const rate = typeof entry.commitRate === 'number' ? entry.commitRate : ratePerCommit;
    const misc = typeof entry.misc === 'number' ? entry.misc : 0;
    const taxes = typeof entry.taxes === 'number' ? entry.taxes : 0;
    const amount = qty * rate + misc + taxes;

    const invoiceId = generateId();
    const journalEntry = {
      id: invoiceId,
      date: new Date().toISOString(),
      entryType: 'service-charge' as const,
      customerId: null,
      customer: '',
      amount,
      quantity: qty || undefined,
      unitRate: qty ? rate : undefined,
      misc: misc || undefined,
      taxes: taxes || undefined,
      notes: entry.description ?? '',
      createdBy: actorId,
    };

    const nextJournalEntries = [journalEntry, ...journalEntries];
    const nextTimeEntries = timeEntries.map((t) => (t.id === entryId ? { ...t, stage: 'invoiced' as const, invoiceJournalEntryId: invoiceId } : t));

    setJournalEntries(nextJournalEntries);
    setTimeEntries(nextTimeEntries);

    // Mark commits billed locally
    if (entry.commitShas?.length) {
      const billedSet = new Set(entry.commitShas.map((s) => s.trim()));
      setCommits((prev) => prev.map((c) => (billedSet.has(c.sha) ? { ...c, billed: true } : c)));
    }

    if (!firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Invoice created locally. Connect Firestore to persist.' });
      return;
    }

    try {
      await updateDoc(firestoreDocRef, { journalEntries: nextJournalEntries, timeEntries: nextTimeEntries });

      const commitsCollectionRef = getFirestoreOverviewCommitsCollectionRef();
      if (db && commitsCollectionRef && entry.commitShas?.length) {
        let batch = writeBatch(db);
        let ops = 0;
        for (const sha of entry.commitShas) {
          batch.update(doc(commitsCollectionRef, sha), { billed: true });
          ops += 1;
          if (ops >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            ops = 0;
          }
        }
        if (ops > 0) await batch.commit();
      }
    } catch (err) {
      try {
        await setDoc(
          firestoreDocRef,
          {
            journalEntries: nextJournalEntries,
            timeEntries: nextTimeEntries,
            commits,
            expenses,
            retainerPaid,
            ratePerCommit,
            repoOwner,
            repoName,
          },
          { merge: true }
        );
      } catch {
        // ignore
      }
    }

    toast({ title: 'Invoice created', description: 'Git order closed and invoice recorded.' });
  }, [timeEntries, journalEntries, generateId, ratePerCommit, actorId, firestoreDocRef, commits, expenses, retainerPaid, repoOwner, repoName]);

  const handleMilestoneDialogOpenChange = (next: boolean | ((prev: boolean) => boolean)) => {
    setMilestoneDialogOpen((prev) => {
      const nextValue = typeof next === 'function' ? next(prev) : next;
      if (!nextValue) {
        setMilestoneTargetCommitSha(null);
      }
      return nextValue;
    });
  };

  const visibleJournalEntries = useMemo(() => {
    if (developerUnlocked || isAdminUser) return journalEntries;
    return journalEntries.filter((entry) => entry.createdBy === actorId);
  }, [actorId, developerUnlocked, isAdminUser, journalEntries]);

  const availableCustomers = useMemo<Customer[]>(() => {
    const internal: Customer = { id: INTERNAL_CUSTOMER_ID, name: INTERNAL_CUSTOMER_NAME, kind: 'internal' };
    const rest = customers.filter((customer) => customer.id !== INTERNAL_CUSTOMER_ID);
    return [internal, ...rest];
  }, [customers]);

  const resolveCustomerLabel = useCallback(
    (customerId: string | null | undefined, fallbackName?: string) => {
      const normalized = (customerId ?? '').trim();
      if (!normalized) return (fallbackName ?? '').trim();
      if (normalized === INTERNAL_CUSTOMER_ID) return INTERNAL_CUSTOMER_NAME;
      const match = availableCustomers.find((customer) => customer.id === normalized);
      return (match?.name ?? '').trim() || (fallbackName ?? '').trim();
    },
    [availableCustomers]
  );

  const createCustomer = useCallback(
    async (rawName: string) => {
      if (!developerUnlocked && !isAdminUser) {
        toast({ title: 'Developer unlock required', description: 'Enter the developer passcode to create customers.' });
        setDeveloperDialogOpen(true);
        return null;
      }

      const name = rawName.trim();
      if (!name) return null;
      if (name.toLowerCase() === INTERNAL_CUSTOMER_NAME.toLowerCase()) {
        toast({ title: 'Reserved name', description: 'Internal is built-in. Pick a different customer name.' });
        return null;
      }

      const existing = availableCustomers.find((customer) => (customer?.name ?? '').trim().toLowerCase() === name.toLowerCase());
      if (existing) return existing.id;

      const nextCustomer: Customer = { id: generateId(), name, kind: 'external' };
      const nextCustomers = [...customers, nextCustomer];
      setCustomers(nextCustomers);

      if (!firestoreDocRef) {
        toast({ title: 'Saved locally', description: 'Connect Firestore to sync customers.' });
        return nextCustomer.id;
      }

      try {
        await setDoc(firestoreDocRef, { customers: nextCustomers }, { merge: true });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to sync customers.';
        toast({ title: 'Sync failed', description: message });
      }

      return nextCustomer.id;
    },
    [availableCustomers, customers, developerUnlocked, firestoreDocRef, isAdminUser]
  );

  const invoiceEntries = useMemo(() => {
    const entries = [...visibleJournalEntries];
    const hasMatchingRetainerEntry =
      retainerPaid > 0 &&
      entries.some((entry) => entry.entryType === 'retainer' && Math.abs(entry.amount - retainerPaid) < 0.005);

    if (!hasMatchingRetainerEntry && retainerPaid > 0) {
      entries.unshift({
        id: 'retainer-paid',
        date: '',
        entryType: 'retainer',
        customer: entries[0]?.customer ?? '—',
        amount: retainerPaid,
        notes: 'Retainer paid',
        createdBy: actorId,
      });
    }

    return entries;
  }, [actorId, retainerPaid, visibleJournalEntries]);

  const totalInvoiceAmount = useMemo(() => {
    return invoiceEntries.reduce((sum, entry) => sum + (Number.isFinite(entry.amount) ? entry.amount : 0), 0);
  }, [invoiceEntries]);

  const buildInvoiceDraft = (entryId: string): InvoiceDraft | null => {
    const entry = visibleJournalEntries.find((candidate) => candidate.id === entryId);
    if (!entry) {
      if (entryId !== 'retainer-paid') return null;
      return {
        id: 'retainer-paid',
        date: '',
        entryType: 'retainer',
        customerId: INTERNAL_CUSTOMER_ID,
        customer: visibleJournalEntries[0]?.customer ?? '—',
        amount: String(retainerPaid ?? ''),
        misc: '',
        taxes: '',
        notes: 'Retainer paid',
      };
    }

    const date = new Date(entry.date);
    return {
      id: entry.id,
      date: Number.isNaN(date.getTime()) ? '' : format(date, 'yyyy-MM-dd'),
      entryType: entry.entryType,
      customerId:
        typeof entry.customerId === 'string' && entry.customerId.trim()
          ? entry.customerId.trim()
          : entry.customer.trim().toLowerCase() === INTERNAL_CUSTOMER_NAME.toLowerCase()
            ? INTERNAL_CUSTOMER_ID
            : null,
      customer: entry.customer,
      amount: String(entry.amount ?? ''),
      quantity: typeof entry.quantity === 'number' ? String(entry.quantity) : undefined,
      unitRate: typeof entry.unitRate === 'number' ? String(entry.unitRate) : undefined,
      misc: typeof entry.misc === 'number' ? String(entry.misc) : '',
      taxes: typeof entry.taxes === 'number' ? String(entry.taxes) : '',
      notes: entry.notes ?? '',
    };
  };

  const openInvoiceEditor = (entryId: string) => {
    if (!developerUnlocked && !isAdminUser) {
      setPendingInvoiceEditId(entryId);
      setDeveloperDialogOpen(true);
      toast({ title: 'Developer unlock required', description: 'Enter the developer passcode to edit invoices.' });
      return;
    }

    const draft = buildInvoiceDraft(entryId);
    if (!draft) {
      toast({
        title: 'Invoice not found',
        description: 'This invoice can only be edited if it was logged as an invoice entry.',
      });
      return;
    }

    if (entryId !== 'retainer-paid') {
      const linkedGitOrderCount = timeEntries.filter(
        (entry) => resolveTimeEntryKind(entry) === 'git-order' && (entry.invoiceJournalEntryId ?? '').trim() === entryId
      ).length;
      if (linkedGitOrderCount === 0) {
        toast({
          title: 'No Git orders linked',
          description: 'This invoice has no Git orders assigned to it.',
        });
      }
    }

    setInvoiceDraft(draft);
    setInvoiceEditorOpen(true);
  };

  const handleSaveInvoiceEdit = async () => {
    if (!invoiceDraft) return;

    const amountNumber = Number(invoiceDraft.amount);
    const miscNumber = invoiceDraft.misc.trim() ? Number(invoiceDraft.misc) : 0;
    const taxesNumber = invoiceDraft.taxes.trim() ? Number(invoiceDraft.taxes) : 0;
    const qtyNumber = invoiceDraft.quantity?.trim() ? Number(invoiceDraft.quantity) : undefined;
    const rateNumber = invoiceDraft.unitRate?.trim() ? Number(invoiceDraft.unitRate) : undefined;
    const selectedCustomerIdRaw = (invoiceDraft.customerId ?? '').trim();
    const selectedCustomerId = selectedCustomerIdRaw === CUSTOMER_CREATE_SENTINEL ? '' : selectedCustomerIdRaw;
    const hasKnownCustomer = !selectedCustomerId || availableCustomers.some((customer) => customer.id === selectedCustomerId);
    if (!hasKnownCustomer) {
      toast({ title: 'Customer not found', description: 'Select an existing customer or create a new one.' });
      return;
    }

    if (!invoiceDraft.date || (!selectedCustomerId && !invoiceDraft.customer.trim()) || !Number.isFinite(amountNumber)) {
      toast({ title: 'Missing details', description: 'Add a date, customer, and valid amount.' });
      return;
    }

    if ((invoiceDraft.misc.trim() && !Number.isFinite(miscNumber)) || (invoiceDraft.taxes.trim() && !Number.isFinite(taxesNumber))) {
      toast({ title: 'Invalid totals', description: 'Misc and Taxes must be valid numbers.' });
      return;
    }

    if (
      (typeof qtyNumber === 'number' && !Number.isFinite(qtyNumber)) ||
      (typeof rateNumber === 'number' && !Number.isFinite(rateNumber))
    ) {
      toast({ title: 'Invalid qty/rate', description: 'Qty and Rate must be valid numbers.' });
      return;
    }

    const customerId = selectedCustomerId ? selectedCustomerId : null;
    const customerLabel = resolveCustomerLabel(customerId, invoiceDraft.customer) || invoiceDraft.customer.trim();

    const updated: JournalEntry = {
      id: invoiceDraft.id,
      date: new Date(invoiceDraft.date).toISOString(),
      entryType: invoiceDraft.id === 'retainer-paid' ? 'retainer' : invoiceDraft.entryType,
      customerId,
      customer: customerLabel,
      amount: amountNumber,
      quantity: typeof qtyNumber === 'number' ? qtyNumber : undefined,
      unitRate: typeof rateNumber === 'number' ? rateNumber : undefined,
      misc: miscNumber ? miscNumber : undefined,
      taxes: taxesNumber ? taxesNumber : undefined,
      notes: invoiceDraft.notes.trim() ? invoiceDraft.notes.trim() : undefined,
      createdBy: actorId,
    };

    const existingIndex = journalEntries.findIndex((entry) => entry.id === updated.id);
    const nextEntries =
      existingIndex >= 0
        ? journalEntries.map((entry) => (entry.id === updated.id ? updated : entry))
        : [updated, ...journalEntries];

    if (updated.id === 'retainer-paid') {
      setRetainerPaid(updated.amount);
    }
    setJournalEntries(nextEntries);
    setInvoiceEditorOpen(false);
    setInvoiceDraft(null);

    if (!firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Connect Firestore to sync invoice edits.' });
      return;
    }

    try {
      await updateDoc(firestoreDocRef, { journalEntries: nextEntries });
    } catch {
      await setDoc(
        firestoreDocRef,
        {
          journalEntries: nextEntries,
          commits,
          timeEntries,
          expenses,
          retainerPaid,
          ratePerCommit,
          repoOwner,
          repoName,
        },
        { merge: true }
      );
    }
  };

  const deleteInvoice = async (invoiceId: string) => {
    if (!developerUnlocked && !isAdminUser) {
      setPendingInvoiceEditId(invoiceId);
      setDeveloperDialogOpen(true);
      toast({ title: 'Developer unlock required', description: 'Enter the developer passcode to delete invoices.' });
      return;
    }

    if (!invoiceId || invoiceId === 'retainer-paid') {
      toast({ title: 'Cannot delete', description: 'This invoice cannot be deleted.' });
      return;
    }

    const existing = journalEntries.find((entry) => entry.id === invoiceId) ?? null;
    if (!existing) {
      toast({ title: 'Invoice not found', description: 'That invoice could not be found.' });
      return;
    }

    const affectedGitOrders = timeEntries.filter((entry) => (entry.invoiceJournalEntryId ?? '').trim() === invoiceId);
    const commitShasToUnbill = affectedGitOrders
      .flatMap((entry) => entry.commitShas ?? [])
      .map((sha) => sha.trim())
      .filter(Boolean);

    const nextJournalEntries = journalEntries.filter((entry) => entry.id !== invoiceId);
    const nextTimeEntries: TimeEntry[] = timeEntries.map((entry): TimeEntry => {
      if ((entry.invoiceJournalEntryId ?? '').trim() !== invoiceId) return entry;
      return {
        ...entry,
        stage: 'workorder' as const,
        invoiceJournalEntryId: null,
      };
    });

    setJournalEntries(nextJournalEntries);
    setTimeEntries(nextTimeEntries);
    if (commitShasToUnbill.length > 0) {
      const unbillSet = new Set(commitShasToUnbill);
      setCommits((prev) =>
        prev.map((commit) =>
          unbillSet.has(commit.sha)
            ? {
                ...commit,
                billed: false,
              }
            : commit
        )
      );
    }

    setInvoiceEditorOpen(false);
    setInvoiceDraft(null);

    if (!firestoreDocRef) {
      toast({ title: 'Deleted locally', description: 'Connect Firestore to sync invoice deletions.' });
      return;
    }

    try {
      if (commitShasToUnbill.length > 0 && db && firestoreCommitsCollectionRef) {
        let batch = writeBatch(db);
        let ops = 0;
        for (const sha of commitShasToUnbill) {
          batch.update(doc(firestoreCommitsCollectionRef, sha), { billed: false });
          ops += 1;
          if (ops >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            ops = 0;
          }
        }
        if (ops > 0) await batch.commit();
      }

      await setDoc(
        firestoreDocRef,
        {
          journalEntries: nextJournalEntries,
          timeEntries: nextTimeEntries,
        },
        { merge: true }
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to sync invoice deletion.';
      toast({ title: 'Sync failed', description: message });
      return;
    }

    toast({ title: 'Invoice deleted', description: affectedGitOrders.length ? 'Reopened linked Git orders.' : 'Removed the invoice entry.' });
  };

  const hydrateFromSnapshot = (data: DeveloperSnapshot | undefined) => {
    if (!data) return;
    if (Array.isArray(data.commits)) {
      const normalizedCommits = data.commits.map((commit) => ({
        ...commit,
        billingAmount: normalizeBillingAmount((commit as any).billingAmount, ratePerCommit),
      }));

      setCommits((prev) => {
        const prevIsFull = prev.length > FIRESTORE_COMMITS_PREVIEW_LIMIT;
        const nextIsPreview = normalizedCommits.length <= FIRESTORE_COMMITS_PREVIEW_LIMIT;
        if (prevIsFull && nextIsPreview) return prev;
        return normalizedCommits;
      });
    }
    if (typeof data.commitCount === 'number') setFirestoreCommitCount(data.commitCount);
    if (typeof data.totalAdditions === 'number') setFirestoreTotalAdditions(data.totalAdditions);
    if (typeof data.totalDeletions === 'number') setFirestoreTotalDeletions(data.totalDeletions);
    if (typeof data.totalStatsCommitCount === 'number') setFirestoreTotalStatsCommitCount(data.totalStatsCommitCount);
    if (typeof data.totalsUpdatedAt === 'string') setFirestoreTotalsUpdatedAt(data.totalsUpdatedAt);
    if (typeof data.commitsUpdatedAt === 'string') setFirestoreCommitsUpdatedAt(data.commitsUpdatedAt);
    if (typeof data.lastCommitSyncAt === 'string') setLastCommitSyncAt(data.lastCommitSyncAt);
    if (data.timeEntries) setTimeEntries(data.timeEntries);
    if (data.expenses) setExpenses(data.expenses);
    if (data.journalEntries) setJournalEntries(data.journalEntries);

    if (Array.isArray(data.customers)) {
      setCustomers(
        data.customers.filter(
          (customer): customer is Customer =>
            !!customer &&
            typeof customer.id === 'string' &&
            !!customer.id.trim() &&
            typeof customer.name === 'string' &&
            !!((customer?.name ?? '').trim())
        )
      );
    } else if (Array.isArray(data.journalEntries) && data.journalEntries.length > 0) {
      const seen = new Set<string>();
      const derived: Customer[] = [];
      for (const entry of data.journalEntries) {
        const name = typeof entry?.customer === 'string' ? entry.customer.trim() : '';
        if (!name) continue;
        if (name.toLowerCase() === INTERNAL_CUSTOMER_NAME.toLowerCase()) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const id = `legacy-${key.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)}`;
        derived.push({ id, name, kind: 'external' });
      }
      if (derived.length) setCustomers(derived);
    }
    if (typeof data.retainerPaid === 'number') setRetainerPaid(data.retainerPaid);
    if (typeof data.ratePerCommit === 'number' && !rateOverrideRef.current) {
      setRatePerCommit(data.ratePerCommit);
      if (!rateDraftDirtyRef.current) {
        setRateDraft(String(data.ratePerCommit));
      }
      setStoredRatePerCommit(data.ratePerCommit);
    }
    if (typeof data.repoOwner === 'string' && !repoOwnerDirtyRef.current) {
      setRepoOwner(data.repoOwner);
      setStoredRepoOwner(data.repoOwner);
    }
    if (typeof data.repoName === 'string' && !repoNameDirtyRef.current) {
      setRepoName(data.repoName);
      setStoredRepoName(data.repoName);
    }
    if (Array.isArray(data.repoFiles)) {
      setRepoFiles(data.repoFiles);
      setRepoFilesStorage('doc');
      setRepoFileCount(data.repoFiles.length);
      setSelectedRepoFile((current) => {
        if (!current) return data.repoFiles?.[0] ?? null;
        return data.repoFiles?.find((file) => file.path === current.path) ?? data.repoFiles?.[0] ?? null;
      });
    }
    if (data.repoFilesStorage === 'subcollection') {
      setRepoFilesStorage('subcollection');
    }
    if (typeof data.repoFileCount === 'number') {
      setRepoFileCount(data.repoFileCount);
    }
    if (typeof data.repoFilesUpdatedAt === 'string') {
      setRepoFilesUpdatedAt(data.repoFilesUpdatedAt);
    }

    if (typeof data.repoSnapshotCommitSha === 'string') {
      setRepoSnapshotCommitSha(data.repoSnapshotCommitSha);
    }

    if (typeof data.repoSnapshotVersion === 'string') {
      setRepoSnapshotVersion(data.repoSnapshotVersion);
    }
  };

  const resetLocalData = () => {
    const storedRate = getStoredRatePerCommit();
    setCommits([]);
    setTimeEntries([]);
    setExpenses([]);
    setJournalEntries([]);
    setRetainerPaid(RETAINER_PAID);
    setRatePerCommit(storedRate);
    setRateDraft(String(storedRate));
    rateDraftDirtyRef.current = false;
    setDataSource('local');
    setLastCommitSyncAt(null);
    setFirestoreCommitCount(null);
    setFirestoreTotalAdditions(null);
    setFirestoreTotalDeletions(null);
    setFirestoreTotalStatsCommitCount(null);
    setFirestoreTotalsUpdatedAt(null);
    setRepoFiles([]);
    setRepoFilesUpdatedAt(null);
    setRepoFilesStorage('doc');
    setRepoFileCount(null);
    setRepoFilesHydratedAt(null);
    setRepoFilesError(null);
    setSelectedRepoFile(null);
    setRepoSnapshotCommitSha(null);
    setRepoSnapshotVersion(null);
    setRepoSnapshotGeneratingCommitSha(null);
    setMilestoneTargetCommitSha(null);
  };

  const fetchCommits = async () => {
    if (!firestoreDocRef) {
      resetLocalData();
      toast({ title: 'Firestore not connected', description: 'Connect Firestore or load data from GitHub.' });
      return;
    }

    setLoading(true);
    try {
      const snapshot = await getDocFromServer(firestoreDocRef);
      if (snapshot.exists()) {
        hydrateFromSnapshot(snapshot.data() as DeveloperSnapshot);
        await loadInitialFirestoreCommits();
        setDataSource('firestore');
        toast({ title: 'Firestore refreshed', description: 'Latest developer data loaded.' });
      } else {
        resetLocalData();
        toast({ title: 'No Firestore data yet', description: 'Load data from GitHub or add it manually.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockDeveloper = useCallback(() => {
    if (developerPasscode === DEVELOPER_PASSCODE || isAdminAuthenticated()) {
      setDeveloperUnlocked(true);
      setDeveloperDialogOpen(false);
      setStoredDeveloperUnlock(true);
      toast({ title: 'Developer unlocked', description: 'Developer controls are now available.' });
    } else {
      toast({ title: 'Incorrect passcode', description: 'The developer passcode is incorrect.' });
    }
  }, [developerPasscode]);

  const handleLockDeveloper = useCallback(() => {
    setDeveloperUnlocked(false);
    setPendingInvoiceEditId(null);
    setStoredDeveloperUnlock(false);
    toast({ title: 'Developer locked', description: 'Developer controls have been locked.' });
  }, []);

  const handleSaveRate = useCallback(async () => {
    const parsed = Number(rateDraft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast({ title: 'Invalid rate', description: 'Enter a valid numeric commit rate.' });
      return;
    }
    setRatePerCommit(parsed);
    setStoredRatePerCommit(parsed);

    if (!firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Rate saved locally.' });
      return;
    }

    try {
      await updateDoc(firestoreDocRef, { ratePerCommit: parsed });
    } catch {
      try {
        await setDoc(firestoreDocRef, { ratePerCommit: parsed }, { merge: true });
      } catch {
        // ignore
      }
    }

    toast({ title: 'Saved', description: 'Default commit rate updated.' });
  }, [rateDraft, firestoreDocRef]);

  const handleSaveHourlyRate = useCallback(async () => {
    const parsed = Number(hourlyRateDraft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast({ title: 'Invalid rate', description: 'Enter a valid numeric hourly rate.' });
      return;
    }
    setStoredRatePerHour(parsed);

    if (!firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Hourly rate saved locally.' });
      return;
    }

    try {
      await updateDoc(firestoreDocRef, { ratePerHour: parsed });
    } catch {
      try {
        await setDoc(firestoreDocRef, { ratePerHour: parsed }, { merge: true });
      } catch {
        // ignore
      }
    }

    toast({ title: 'Saved', description: 'Hourly rate updated.' });
  }, [hourlyRateDraft, firestoreDocRef]);

  const handleSaveRepoSettings = useCallback(async () => {
    const owner = (repoOwner ?? '').trim();
    const name = (repoName ?? '').trim();
    if (!owner || !name) {
      toast({ title: 'Missing repo', description: 'Provide both repo owner and name.' });
      return;
    }

    setStoredRepoOwner(owner);
    setStoredRepoName(name);

    if (!firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Repo settings saved locally.' });
      return;
    }

    try {
      await updateDoc(firestoreDocRef, { repoOwner: owner, repoName: name });
    } catch {
      try {
        await setDoc(firestoreDocRef, { repoOwner: owner, repoName: name }, { merge: true });
      } catch {
        // ignore
      }
    }

    toast({ title: 'Saved', description: 'Repository settings updated.' });
  }, [repoOwner, repoName, firestoreDocRef]);

  const deleteTimeEntry = useCallback(async (timeEntryId: string) => {
    if (!developerUnlocked && !isAdminUser) {
      setPendingInvoiceEditId(timeEntryId);
      setDeveloperDialogOpen(true);
      toast({ title: 'Developer unlock required', description: 'Enter the developer passcode to delete entries.' });
      return;
    }

    const existing = timeEntries.find((t) => t.id === timeEntryId) ?? null;
    if (!existing) {
      toast({ title: 'Not found', description: 'That time entry could not be found.' });
      return;
    }

    const nextTimeEntries = timeEntries.filter((t) => t.id !== timeEntryId);
    setTimeEntries(nextTimeEntries);

    // Unmark commits as billed if this was a git order
    if (existing.commitShas?.length) {
      const unbillSet = new Set(existing.commitShas.map((s) => s.trim()));
      setCommits((prev) => prev.map((c) => (unbillSet.has(c.sha) ? { ...c, billed: false } : c)));
    }

    if (!firestoreDocRef) {
      toast({ title: 'Deleted locally', description: 'Time entry removed locally.' });
      return;
    }

    try {
      await updateDoc(firestoreDocRef, { timeEntries: nextTimeEntries });
    } catch {
      try {
        await setDoc(firestoreDocRef, { timeEntries: nextTimeEntries }, { merge: true });
      } catch {
        // ignore
      }
    }

    toast({ title: 'Deleted', description: 'Time entry removed.' });
  }, [developerUnlocked, isAdminUser, timeEntries, firestoreDocRef]);

  const handleReloadAllCommits = useCallback(async () => {
    try {
      setLoading(true);
      await fetchAllCommitsFromGitHub();
      toast({ title: 'Commits reloaded', description: 'Fetched commits from GitHub.' });
    } catch (err) {
      toast({ title: 'Reload failed', description: 'Unable to reload commits.' });
    } finally {
      setLoading(false);
    }
  }, [fetchAllCommitsFromGitHub]);

  const confirmAdminDownloadRepoZip = useCallback(async () => {
    // Trigger the download flow: ask the backend to prepare a snapshot and then open the generated URL.
    // For now, simply close the dialog and simulate a download via existing fetchRepoSnapshot if available.
    try {
      if (!repoSnapshotCommitSha) {
        // If no snapshot SHA is available, attempt to fetch one
        await fetchRepoSnapshot();
      }
    } catch (e) {
      // ignore
    }

    setDownloadReason('');
    setDownloadReasonDialogOpen(false);
    toast({ title: 'Download started', description: 'Preparing repository ZIP download.' });
  }, [repoSnapshotCommitSha, fetchRepoSnapshot, setDownloadReasonDialogOpen]);

  const {
    handleAddTimeEntry,
    createGitOrderFromSelectedCommits,
    handleAddExpense,
    handleAddJournalEntry,
  } = useDeveloperFinanceActions({
    commits,
    expenses,
    journalEntries,
    availableCustomers,
    timeEntries,
    firestoreCommitsCollectionRef,
    firestoreDocRef,
    ratePerCommit,
    retainerPaid,
    repoOwner,
    repoName,
    internalCustomerName: INTERNAL_CUSTOMER_NAME,
    customerCreateSentinel: CUSTOMER_CREATE_SENTINEL,
    actorId,
    internalCustomerId: INTERNAL_CUSTOMER_ID,
    developerUnlocked,
    isAdminUser,
    resolveCustomerLabel,
    generateId,
    timeDate,
    timeHours,
    timeHourlyRate,
    timeDescription,
    gitOrderDescription,
    timeMisc,
    timeTaxes,
    timeEntryCommitShas,
    timeEntryMarkCommitsComplete,
    expenseDate,
    expenseCategory,
    expenseAmount,
    expenseMisc,
    expenseTaxes,
    expenseNotes,
    expenseJournalEntry,
    expenseAttachment,
    journalDate,
    journalType,
    journalCustomerId,
    journalAmount,
    journalMisc,
    journalTaxes,
    journalNotes,
    setDeveloperDialogOpen,
    setCommits,
    setTimeEntries,
    setTimeDate,
    setTimeHours,
    setTimeDescription,
    setGitOrderDescription,
    setTimeMisc,
    setTimeTaxes,
    setTimeEntryCommitShas,
    setTimeEntryMarkCommitsComplete,
    setExpenses,
    setExpenseDate,
    setExpenseCategory,
    setExpenseAmount,
    setExpenseMisc,
    setExpenseTaxes,
    setExpenseNotes,
    setExpenseJournalEntry,
    setExpenseAttachment,
    setJournalEntries,
    setJournalDate,
    setJournalType,
    setJournalCustomerId,
    setJournalAmount,
    setJournalMisc,
    setJournalTaxes,
    setJournalNotes,
  });

  // filteredCommits/filteredCommitShas are computed by useCommitHistoryFilters.

  const {
    resolveTimeEntryKind,
    timeTickets,
    gitOrders,
    selectedWorkorder,
    commitsForSelectedWorkorder,
    commitsForSelectedTimeDate,
    assignableWorkorders,
    selectedAssignCommits,
    primaryAssignCommit,
    openAssignCommitsToGitOrderDialog,
    openAssignCommitToGitOrderDialog,
    assignCommitsToGitOrder,
    createGitOrderFromCommits,
    openWorkorderCommitsDialog,
    saveWorkorderCommits,
    openInvoiceForGitOrder,
  } = useDeveloperWorkorders({
    commits,
    timeEntries,
    journalEntries,
    workorderDialogEntryId,
    workorderDialogCommitShas,
    workorderDialogRate,
    workorderDialogHours,
    workorderDialogHourlyRate,
    workorderDialogMisc,
    workorderDialogTaxes,
    timeDate,
    assignCommitDialogShas,
    assignCommitDialogWorkorderId,
    assignCommitDialogNewWorkorderDescription,
    developerUnlocked,
    isAdminUser,
    ratePerCommit,
    firestoreCommitsCollectionRef,
    firestoreDocRef,
    openInvoiceEditor,
    closeGitOrder,
    generateId,
    setDeveloperDialogOpen,
    setAssignCommitDialogShas,
    setAssignCommitDialogWorkorderId,
    setAssignCommitDialogNewWorkorderDescription,
    setAssignCommitDialogOpen,
    setAssignCommitDialogSaving,
    setAssignCommitDialogCreating,
    setTimeEntries,
    setCommits,
    setWorkorderDialogEntryId,
    setWorkorderDialogCommitShas,
    setWorkorderDialogRate,
    setWorkorderDialogHours,
    setWorkorderDialogHourlyRate,
    setWorkorderDialogMisc,
    setWorkorderDialogTaxes,
    setWorkorderDialogOpen,
    setWorkorderDialogSaving,
  });

  useEffect(() => {
    setTimeEntryCommitShas([]);
    setTimeEntryMarkCommitsComplete(true);
  }, [timeDate]);

  const localTotals = useMemo(() => {
    return commits.reduce(
      (acc, commit) => {
        if (typeof commit.additions === 'number') acc.additions += commit.additions;
        if (typeof commit.deletions === 'number') acc.deletions += commit.deletions;
        if (typeof commit.additions === 'number' && typeof commit.deletions === 'number') acc.statsCommitCount += 1;
        return acc;
      },
      { additions: 0, deletions: 0, statsCommitCount: 0 }
    );
  }, [commits]);

  const displayedTotals = useMemo(() => {
    const additions = typeof firestoreTotalAdditions === 'number' ? firestoreTotalAdditions : localTotals.additions;
    const deletions = typeof firestoreTotalDeletions === 'number' ? firestoreTotalDeletions : localTotals.deletions;
    const statsCommitCount =
      typeof firestoreTotalStatsCommitCount === 'number' ? firestoreTotalStatsCommitCount : localTotals.statsCommitCount;
    return { additions, deletions, statsCommitCount };
  }, [firestoreTotalAdditions, firestoreTotalDeletions, firestoreTotalStatsCommitCount, localTotals]);

  const assignmentFilteredCommits = useMemo(() => {
    if (commitAssignmentFilter === 'all') return filteredCommits;
    if (commitAssignmentFilter === 'assigned') {
      return filteredCommits.filter((commit) => (commit.workorderId ?? '').trim().length > 0);
    }
    return filteredCommits.filter((commit) => (commit.workorderId ?? '').trim().length === 0);
  }, [commitAssignmentFilter, filteredCommits]);

  const assignmentFilteredCommitShas = useMemo(() => {
    return assignmentFilteredCommits.map((commit) => commit.sha);
  }, [assignmentFilteredCommits]);

  useEffect(() => {
    setCommitPage(1);
  }, [commitAssignmentFilter, commitFilter, searchQuery, commitsPerPage]);

  const totalCommitPages = useMemo(() => {
    return Math.max(1, Math.ceil(assignmentFilteredCommits.length / commitsPerPage));
  }, [assignmentFilteredCommits.length, commitsPerPage]);

  const clampedCommitPage = Math.min(Math.max(commitPage, 1), totalCommitPages);

  useEffect(() => {
    if (commitPage !== clampedCommitPage) {
      setCommitPage(clampedCommitPage);
    }
  }, [commitPage, clampedCommitPage]);

  const pagedCommits = useMemo(() => {
    const startIndex = (clampedCommitPage - 1) * commitsPerPage;
    return assignmentFilteredCommits.slice(startIndex, startIndex + commitsPerPage);
  }, [assignmentFilteredCommits, clampedCommitPage, commitsPerPage]);

  const handleNextCommitPage = useCallback(async () => {
    if (clampedCommitPage < totalCommitPages) {
      setCommitPage((prev) => Math.min(totalCommitPages, prev + 1));
      return;
    }

    // If we're at the end of what's currently loaded, page in older commits from Firestore.
    if (!canLoadMoreFirestoreCommits) return;
    const loaded = await loadMoreFirestoreCommits();
    if (loaded) {
      setCommitPage((prev) => prev + 1);
    }
  }, [canLoadMoreFirestoreCommits, clampedCommitPage, loadMoreFirestoreCommits, totalCommitPages]);

  const commitCalendarDays = useMemo(() => {
    const today = new Date();
    const weeks = commitActivityWeeks;
    const daysPerWeek = 7;
    const totalDays = weeks * daysPerWeek;

    const startWeek = startOfWeek(today, { weekStartsOn: 0 });
    const startDate = startOfDay(subWeeks(startWeek, weeks - 1));

    const dayCounts = new Map<string, number>();
    commits.forEach((commit) => {
      const committedAt = new Date(commit.date);
      if (Number.isNaN(committedAt.getTime())) return;
      const key = format(startOfDay(committedAt), 'yyyy-MM-dd');
      dayCounts.set(key, (dayCounts.get(key) ?? 0) + 1);
    });

    const days: Array<{ date: Date; key: string; count: number }> = new Array(totalDays);
    for (let index = 0; index < totalDays; index += 1) {
      const date = addDays(startDate, index);
      const key = format(date, 'yyyy-MM-dd');
      days[index] = { date, key, count: dayCounts.get(key) ?? 0 };
    }
    return days;
  }, [commitActivityWeeks, commits]);

  const billableCommits = useMemo(() => {
    return commits.filter((commit) => !isPullRequestCommit(commit));
  }, [commits]);

  const totalBilled = useMemo(() => {
    return billableCommits
      .filter((commit) => commit.billed)
      .reduce((sum, commit) => sum + normalizeBillingAmount(commit.billingAmount, ratePerCommit), 0);
  }, [billableCommits, ratePerCommit]);

  // Pending should reflect unbilled commits at the current commit rate (no per-commit overrides).
  const totalUnbilled = useMemo(() => {
    const unbilledCount = billableCommits.filter((commit) => !commit.billed).length;
    return unbilledCount * ratePerCommit;
  }, [billableCommits, ratePerCommit]);

  const totalCommitCost = useMemo(() => {
    return totalBilled + totalUnbilled;
  }, [totalBilled, totalUnbilled]);

  const sortedRepoFiles = useMemo(() => {
    return [...repoFiles].sort((a, b) => a.path.localeCompare(b.path));
  }, [repoFiles]);

  const totalHours = useMemo(() => {
    return timeTickets.reduce((sum, entry) => sum + entry.hours, 0);
  }, [timeTickets]);

  const totalExpenses = useMemo(() => {
    return expenses.reduce((sum, entry) => sum + entry.amount, 0);
  }, [expenses]);

  useEffect(() => {
    if (!firestoreDocRef) {
      resetLocalData();
      return;
    }
    if (!developerUnlocked && !isAdminUser) {
      return;
    }

    const unsubscribe = onSnapshot(
      firestoreDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          hydrateFromSnapshot(snapshot.data() as DeveloperSnapshot);
          setDataSource('firestore');
        } else {
          resetLocalData();
        }
      },
      () => {
        resetLocalData();
      }
    );

    return () => unsubscribe();
  }, [firestoreDocRef, developerUnlocked, isAdminUser]);

  useEffect(() => {
    if (!isAdminUser || !firestoreCommitsCollectionRef) return;

    // Once we've hydrated Firestore commits for the current Firestore revision, don't reset the list.
    // This prevents a feedback loop where paging (commits.length changes) triggers another initial load.
    if (dataSource === 'firestore' && firestoreCommitsUpdatedAt && firestoreCommitsHydratedAtRef.current === firestoreCommitsUpdatedAt) {
      return;
    }

    const shouldHydrateFullHistory =
      dataSource !== 'firestore' &&
      (commits.length === 0 ||
        commits.length <= FIRESTORE_COMMITS_PREVIEW_LIMIT ||
        (typeof firestoreCommitCount === 'number' && firestoreCommitCount > commits.length));

    if (!shouldHydrateFullHistory) return;

    let cancelled = false;
    (async () => {
      try {
        await loadInitialFirestoreCommits();
        if (!cancelled && firestoreCommitsUpdatedAt) {
          firestoreCommitsHydratedAtRef.current = firestoreCommitsUpdatedAt;
        }
      } catch {
        // Non-fatal: admins can still manually load commits via the dialog.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isAdminUser,
    firestoreCommitsCollectionRef,
    firestoreCommitsUpdatedAt,
    firestoreCommitCount,
    commits.length,
    dataSource,
    loadInitialFirestoreCommits,
  ]);

  useEffect(() => {
    if (!isAdminUser || !firestoreCommitsCollectionRef) return;
    if (dataSource !== 'firestore') return;
    if (firestoreCommitsPagingLoading) return;
    if (!canLoadMoreFirestoreCommits) return;
    if (commits.length === 0) return;

    // Auto-page in enough commits so the 12-month activity window is accurate.
    const activityStart = startOfDay(
      subWeeks(startOfWeek(new Date(), { weekStartsOn: 0 }), Math.max(0, commitActivityWeeks - 1))
    );
    const oldestLoaded = commits[commits.length - 1];
    const oldestDate = oldestLoaded ? new Date(oldestLoaded.date) : null;
    if (!oldestDate || Number.isNaN(oldestDate.getTime())) return;
    if (oldestDate <= activityStart) return;

    void loadMoreFirestoreCommits();
  }, [
    canLoadMoreFirestoreCommits,
    commits,
    commitActivityWeeks,
    dataSource,
    firestoreCommitsCollectionRef,
    firestoreCommitsPagingLoading,
    isAdminUser,
    loadMoreFirestoreCommits,
  ]);

  useEffect(() => {
    const syncAdminState = () => {
      setIsAdminUser(isAdminAuthenticated() || isBetaAdminAuthenticated());
    };

    syncAdminState();
    window.addEventListener('storage', syncAdminState);
    document.addEventListener('visibilitychange', syncAdminState);

    return () => {
      window.removeEventListener('storage', syncAdminState);
      document.removeEventListener('visibilitychange', syncAdminState);
    };
  }, []);

  // Stub implementations for missing functions
  const billCommitAtRate = useCallback(async (_sha: string, _rate?: number) => {
    toast({ title: 'Not implemented', description: 'Commit billing is not yet implemented.' });
  }, []);

  const unbillCommit = useCallback(async (_sha: string) => {
    toast({ title: 'Not implemented', description: 'Commit unbilling is not yet implemented.' });
  }, []);

  const beginDownloadRepoZip = useCallback(async () => {
    toast({ title: 'Download started', description: 'Preparing ZIP download.' });
    // Stub: trigger download via fetchRepoSnapshot if available
    try {
      await fetchRepoSnapshot();
    } catch {
      // ignore
    }
  }, [fetchRepoSnapshot]);

  const createMilestone = useCallback(async () => {
    if (!milestoneTargetCommitSha || !milestoneTitle.trim()) {
      toast({ title: 'Missing details', description: 'Provide a commit and title for the milestone.' });
      return;
    }
    setMilestoneSaving(true);
    try {
      if (firestoreMilestonesCollectionRef) {
        const newMilestone: Partial<Milestone> = {
          id: generateId(),
          title: milestoneTitle.trim(),
          createdAt: new Date().toISOString(),
          toCommitSha: milestoneTargetCommitSha,
          fromCommitSha: null,
          commitCount: 0,
          snapshotVersion: null,
          state: 'open',
          description: null,
          dueOn: null,
          github: { number: null, htmlUrl: null, apiUrl: null },
        };
        await setDoc(doc(firestoreMilestonesCollectionRef, newMilestone.id!), newMilestone);
        toast({ title: 'Milestone created' });
        setMilestoneDialogOpen(false);
        setMilestoneTitle('');
        setMilestoneTargetCommitSha(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to create milestone.';
      toast({ title: 'Error', description: message });
    } finally {
      setMilestoneSaving(false);
    }
  }, [milestoneTargetCommitSha, milestoneTitle, firestoreMilestonesCollectionRef, generateId]);

  const deleteSnapshot = useCallback(async () => {
    setDeleteSnapshotLoading(true);
    try {
      await deleteCurrentSnapshotFilesFromFirestore();
      toast({ title: 'Snapshot deleted' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete snapshot.';
      toast({ title: 'Error', description: message });
    } finally {
      setDeleteSnapshotLoading(false);
      setDeleteSnapshotDialogOpen(false);
    }
  }, [deleteCurrentSnapshotFilesFromFirestore]);

  const linkTimeTicketToGitOrder = useCallback(async (timeTicketId: string, gitOrderId: string | null) => {
    const nextTimeEntries = timeEntries.map((entry) =>
      entry.id === timeTicketId ? { ...entry, gitOrderId } : entry
    );
    setTimeEntries(nextTimeEntries);

    if (firestoreDocRef) {
      try {
        await updateDoc(firestoreDocRef, { timeEntries: nextTimeEntries });
      } catch {
        try {
          await setDoc(firestoreDocRef, { timeEntries: nextTimeEntries }, { merge: true });
        } catch {
          // ignore
        }
      }
    }

    toast({ title: 'Linked', description: gitOrderId ? 'Time ticket linked to Git order.' : 'Time ticket unlinked.' });
  }, [timeEntries, firestoreDocRef]);

  const dataSourceLabel = dataSource === 'firestore' ? 'Synced' : dataSource === 'github' ? 'GitHub' : 'Local only';

  const navItems = useMemo(() => {
    const items = [
      { id: 'dashboard', label: 'Dashboard', icon: <Grid3X3 className="w-4 h-4" />, to: '/admin' },
      { id: 'appadmin', label: 'App Admin', icon: <Settings className="w-4 h-4" />, to: '/appadmin' },
      { id: 'plans', label: 'Plans', icon: <Navigation className="w-4 h-4" />, to: '/plans' },
      { id: 'developer', label: 'Developer', icon: <Code2 className="w-4 h-4" />, to: '/developer' },
    ];

    if (developerUnlocked) {
      items.splice(3, 0, { id: 'roles', label: 'User roles', icon: <Shield className="w-4 h-4" />, to: '/roles' });
    }

    return items;
  }, [developerUnlocked]);

  const location = useLocation();

  const devicePreviewSrc = useMemo(() => {
    if (typeof window === 'undefined') return '/developer?embedded=1';
    const url = new URL(window.location.href);
    url.searchParams.set('embedded', '1');
    url.searchParams.set('previewDevice', devicePreview);
    url.searchParams.delete('preview');
    return `${url.pathname}${url.search}`;
  }, [devicePreview]);

  const isDevicePreviewActive = !isCompactView && !isPreviewEmbedded && devicePreview !== 'full';

  return (
    <div className="h-[100dvh] bg-background nebula-bg star-field flex flex-col overflow-hidden">
      <main
        className="container mx-auto px-4 pt-4 pb-[calc(3.5rem+var(--safe-area-bottom,0px))] md:pb-8 flex-1 overflow-y-auto"
        style={
          isPreviewEmbedded
            ? ({
                ['--safe-area-bottom' as never]: `${previewSafeAreaBottom}px`,
              } as CSSProperties)
            : undefined
        }
      >
        {!isPreviewEmbedded && (
          <nav
            className="sticky top-0 z-40 mb-4 mx-4 mt-2"
            style={{ paddingTop: 'var(--safe-area-top, 0px)' }}
            aria-label="Developer navigation"
          >
            <div className="mx-auto rounded-2xl border border-border/60 bg-background/85 backdrop-blur px-2 py-2 shadow-[0_12px_30px_rgba(0,0,0,0.25)]">
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar px-1">
                {navItems.map((item) => {
                  const active = location.pathname.startsWith(item.to ?? '/');
                  return (
                    <Button
                      key={item.id}
                      asChild
                      variant={active ? 'default' : 'ghost'}
                      size="sm"
                      className="gap-2 rounded-full px-3 py-2"
                    >
                      <Link to={item.to ?? '#'} aria-current={active ? 'page' : undefined} className="flex items-center gap-2">
                        {item.icon}
                        <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                      </Link>
                    </Button>
                  );
                })}
              </div>
            </div>
          </nav>
        )}

        {!isPreviewEmbedded && (
          <div className="mb-6 flex flex-col gap-4">
            <ComingSoonToggleCard />
            <BetaEnableCard />
            <BetaDashboardVisibilityCard />
          </div>
        )}

        {!isCompactView && !isPreviewEmbedded && devicePreview === 'full' && (
          <DevicePreviewControls
            kind={devicePreviewKind}
            onKindChange={setDevicePreviewKind}
            value={devicePreview}
            onChange={setDevicePreview}
          />
        )}

        {isDevicePreviewActive ? (
          <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
            <div className="flex-1">
              <DevicePreviewFrame device={devicePreview} src={devicePreviewSrc} title="Developer preview" />
            </div>

            <div className="w-full lg:w-[360px]">
              <DevicePreviewControls
                kind={devicePreviewKind}
                onKindChange={setDevicePreviewKind}
                value={devicePreview}
                onChange={setDevicePreview}
              />
              <p className="text-xs text-muted-foreground">
                Preview uses a real viewport (iframe) so mobile breakpoints, bottom tabs, and overflow behavior match iOS/Android.
              </p>
            </div>
          </div>
        ) : (
          <>
          <MobileDeveloperToolsBar
            enabled={isCompactView}
            onOpenTools={() => setDeveloperDialogOpen(true)}
            locked={!developerUnlocked && !isAdminUser}
          />

          {(!isCompactView || mobileSection === 'overview') && (
            <DeveloperOverviewHeader
              developerUnlocked={developerUnlocked}
              canViewCommitData={canViewCommitData}
              isAdminUser={isAdminUser}
              commitsCount={billableCommits.length}
              ratePerCommit={ratePerCommit}
              totalBilled={totalBilled}
              totalUnbilled={totalUnbilled}
              retainerPaid={retainerPaid}
              totalCommitCost={totalCommitCost}
              isFirebaseConfigured={isFirebaseConfigured}
              dataSourceLabel={dataSourceLabel}
              dataSource={dataSource}
              commitCalendarDays={commitCalendarDays}
              commitActivityLabel={commitActivityLabel}
              onOpenDeveloperDialog={() => setDeveloperDialogOpen(true)}
              onLockView={handleLockDeveloper}
            />
          )}

          <DeveloperControlsDialog
          open={developerDialogOpen}
          onOpenChange={(open) => {
            setDeveloperDialogOpen(open);
            setRateDraft(String(ratePerCommit));
            setHourlyRateDraft(getStoredRatePerHour().toFixed(2));
            rateDraftDirtyRef.current = false;
            hourlyRateDraftDirtyRef.current = false;
            repoOwnerDirtyRef.current = false;
            repoNameDirtyRef.current = false;
            if (!open) {
              setDeveloperPasscode('');
              setStoredDeveloperUnlock(developerUnlocked);
              if (!developerUnlocked) {
                setPendingInvoiceEditId(null);
              }
            }
          }}
          canManageDeveloperSettings={canManageDeveloperSettings}
          developerPasscode={developerPasscode}
          setDeveloperPasscode={setDeveloperPasscode}
          handleUnlockDeveloper={handleUnlockDeveloper}
          rateDraft={rateDraft}
          setRateDraft={setRateDraft}
          rateDraftDirtyRef={rateDraftDirtyRef}
          handleSaveRate={handleSaveRate}
          hourlyRateDraft={hourlyRateDraft}
          setHourlyRateDraft={setHourlyRateDraft}
          hourlyRateDraftDirtyRef={hourlyRateDraftDirtyRef}
          handleSaveHourlyRate={handleSaveHourlyRate}
          lastGitHubSyncError={lastGitHubSyncError}
          repoOwner={repoOwner}
          setRepoOwner={setRepoOwner}
          repoOwnerDirtyRef={repoOwnerDirtyRef}
          repoName={repoName}
          setRepoName={setRepoName}
          repoNameDirtyRef={repoNameDirtyRef}
          handleSaveRepoSettings={handleSaveRepoSettings}
          loadingNewCommits={loadingNewCommits}
          fetchNewCommitsFromGitHub={fetchNewCommitsFromGitHub}
          gitHubProxyPingLoading={gitHubProxyPingLoading}
          handlePingGitHubProxy={handlePingGitHubProxy}
          developerUnlocked={developerUnlocked}
          isAdminUser={isAdminUser}
          handleCheckGitHubAuth={handleCheckGitHubAuth}
          repoFilesLoading={repoFilesLoading}
          repoSnapshotCommitSha={repoSnapshotCommitSha}
          fetchRepoSnapshot={() => fetchRepoSnapshot()}
          loadingAllCommits={loadingAllCommits}
          setReloadDialogOpen={setReloadDialogOpen}
          expenseDate={expenseDate}
          setExpenseDate={setExpenseDate}
          expenseCategory={expenseCategory}
          setExpenseCategory={setExpenseCategory}
          expenseAmount={expenseAmount}
          setExpenseAmount={setExpenseAmount}
          expenseAttachment={expenseAttachment}
          setExpenseAttachment={setExpenseAttachment}
          expenseNotes={expenseNotes}
          setExpenseNotes={setExpenseNotes}
          expenseJournalEntry={expenseJournalEntry}
          setExpenseJournalEntry={setExpenseJournalEntry}
          handleAddExpense={handleAddExpense}
        />

        {(!isCompactView || mobileSection === 'invoices') && (
          <InvoicesCard
            developerUnlocked={developerUnlocked}
            isFirebaseConfigured={isFirebaseConfigured}
            workorders={gitOrders}
            defaultCommitRate={ratePerCommit}
            closeGitOrder={closeGitOrder}
            openInvoiceForGitOrder={openInvoiceForGitOrder}
            openWorkorderCommitsDialog={openWorkorderCommitsDialog}
            deleteGitOrder={deleteTimeEntry}
            journalDate={journalDate}
            setJournalDate={setJournalDate}
            customers={availableCustomers}
            journalCustomerId={journalCustomerId}
            setJournalCustomerId={setJournalCustomerId}
            journalAmount={journalAmount}
            setJournalAmount={setJournalAmount}
            journalMisc={journalMisc}
            setJournalMisc={setJournalMisc}
            journalTaxes={journalTaxes}
            setJournalTaxes={setJournalTaxes}
            journalType={journalType}
            setJournalType={setJournalType}
            journalNotes={journalNotes}
            setJournalNotes={setJournalNotes}
            createCustomer={createCustomer}
            handleAddJournalEntry={handleAddJournalEntry}
            onOpenDeveloperDialog={() => setDeveloperDialogOpen(true)}
            invoiceEntries={invoiceEntries}
            totalInvoiceAmount={totalInvoiceAmount}
            openInvoiceEditor={openInvoiceEditor}
          />
        )}

        {developerUnlocked && (!isCompactView || mobileSection === 'invoices') && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FinancialSnapshotCard
              customers={availableCustomers}
              journalEntries={journalEntries}
              timeEntries={timeEntries}
              defaultCommitRate={ratePerCommit}
            />

            <BillingCard />

            <ICSStatusCard />
          </div>
        )}

        <InvoiceEditorDialog
          open={invoiceEditorOpen}
          onOpenChange={(open) => {
            setInvoiceEditorOpen(open);
            if (!open) setInvoiceDraft(null);
          }}
          invoiceDraft={invoiceDraft}
          setInvoiceDraft={setInvoiceDraft}
          onCancel={() => setInvoiceEditorOpen(false)}
          handleSaveInvoiceEdit={handleSaveInvoiceEdit}
          onDeleteInvoice={deleteInvoice}
          customers={availableCustomers}
          createCustomer={createCustomer}
        />

        {canViewCommitData && (!isCompactView || mobileSection === 'commits') && (
          <CommitHistoryCard
            developerUnlocked={developerUnlocked}
            canManageGitOrders={developerUnlocked}
            commitBillingSha={commitBillingSha}
            billCommit={billCommitAtRate}
            unbillCommit={unbillCommit}
            onAssignCommitToGitOrder={openAssignCommitToGitOrderDialog}
            onOpenGitOrder={openWorkorderCommitsDialog}
            onAssignFilteredCommitsToGitOrder={openAssignCommitsToGitOrderDialog}
            lastCommitSyncAt={lastCommitSyncAt}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            commitFilter={commitFilter}
            setCommitFilter={setCommitFilter}
            commitAssignmentFilter={commitAssignmentFilter}
            setCommitAssignmentFilter={setCommitAssignmentFilter}
            commitsPerPage={commitsPerPage}
            setCommitsPerPage={setCommitsPerPage}
            clampedCommitPage={clampedCommitPage}
            totalCommitPages={totalCommitPages}
            setCommitPage={setCommitPage}
            handleNextCommitPage={handleNextCommitPage}
            displayedTotals={displayedTotals}
            firestoreTotalAdditions={firestoreTotalAdditions}
            firestoreTotalDeletions={firestoreTotalDeletions}
            dataSource={dataSource}
            firestoreCommitCount={firestoreCommitCount}
            commitsTotal={commits.length}
            firestoreCommitsLoadedCount={firestoreCommitsLoadedCount}
            firestoreCommitsPagingLoading={firestoreCommitsPagingLoading}
            canLoadMoreFirestoreCommits={canLoadMoreFirestoreCommits}
            firestoreCommitsPagingError={firestoreCommitsPagingError}
            pagedCommits={pagedCommits}
            filteredCommitsLength={assignmentFilteredCommits.length}
            filteredCommitShas={assignmentFilteredCommitShas}
            ratePerCommit={ratePerCommit}
          />
        )}

        {canViewRepoSnapshot && (!isCompactView || mobileSection === 'releases') && (
          <>
            {isCompactView ? (
              <PageController
                storageKey="developerReleasesPage"
                dockIndicators
                pages={[
                  {
                    key: 'published-builds',
                    label: 'Published Builds',
                    content: (
                      <PublishedBuildsCard
                        buildGitSha={BUILD_GIT_SHA}
                        buildTimeUtc={BUILD_TIME_UTC}
                        developerUnlocked={developerUnlocked}
                        restrictToMilestones={isAdminUser && !developerUnlocked}
                        hostingDeployments={hostingDeployments}
                        hostingDeploymentsLoading={hostingDeploymentsLoading}
                        hostingDeploymentsError={hostingDeploymentsError}
                        repoSnapshotCommitSha={repoSnapshotCommitSha}
                        repoSnapshotVersion={repoSnapshotVersion}
                        repoFilesLoading={repoFilesLoading}
                        repoSnapshotGeneratingCommitSha={repoSnapshotGeneratingCommitSha}
                        milestones={milestones}
                        openMilestoneEditor={openMilestoneEditor}
                        onViewCommit={viewCommitInHistory}
                        fetchRepoSnapshot={({ commitSha, buildTimeUtc }) =>
                          fetchRepoSnapshot({
                            commitSha,
                            buildTimeUtc,
                          })
                        }
                        beginDownloadRepoZip={beginDownloadRepoZip}
                      />
                    ),
                  },
                  {
                    key: 'code-snapshot',
                    label: 'Code Snapshot',
                    content: (
                      <CodeSnapshotCard
                        repoFiles={repoFiles}
                        sortedRepoFiles={sortedRepoFiles}
                        repoFilesUpdatedAt={repoFilesUpdatedAt}
                        repoFilesError={repoFilesError}
                        repoFilesLoading={repoFilesLoading}
                        repoFileCount={repoFileCount}
                        repoSnapshotVersion={repoSnapshotVersion}
                        repoSnapshotCommitSha={repoSnapshotCommitSha}
                        developerUnlocked={developerUnlocked}
                        isAdminUser={isAdminUser}
                        selectedRepoFile={selectedRepoFile}
                        setSelectedRepoFile={setSelectedRepoFile}
                        beginDownloadRepoZip={beginDownloadRepoZip}
                        milestones={milestones}
                        milestonesLoading={milestonesLoading}
                        milestonesError={milestonesError}
                        milestoneTargetCommitSha={milestoneTargetCommitSha}
                        milestoneDialogOpen={milestoneDialogOpen}
                        setMilestoneDialogOpen={handleMilestoneDialogOpenChange}
                        milestoneTitle={milestoneTitle}
                        setMilestoneTitle={setMilestoneTitle}
                        milestoneSaving={milestoneSaving}
                        createMilestone={createMilestone}
                        deleteSnapshotDialogOpen={deleteSnapshotDialogOpen}
                        setDeleteSnapshotDialogOpen={setDeleteSnapshotDialogOpen}
                        deleteSnapshotLoading={deleteSnapshotLoading}
                        deleteSnapshot={deleteSnapshot}
                        onJumpToPublishedBuilds={
                          () => {
                            goToSection('releases', 'published-builds');
                          }
                        }
                        onViewCommit={viewCommitInHistory}
                      />
                    ),
                  },
                ]}
              />
            ) : (
              <>
                <PublishedBuildsCard
                  buildGitSha={BUILD_GIT_SHA}
                  buildTimeUtc={BUILD_TIME_UTC}
                  developerUnlocked={developerUnlocked}
                  restrictToMilestones={isAdminUser && !developerUnlocked}
                  hostingDeployments={hostingDeployments}
                  hostingDeploymentsLoading={hostingDeploymentsLoading}
                  hostingDeploymentsError={hostingDeploymentsError}
                  repoSnapshotCommitSha={repoSnapshotCommitSha}
                  repoSnapshotVersion={repoSnapshotVersion}
                  repoFilesLoading={repoFilesLoading}
                  repoSnapshotGeneratingCommitSha={repoSnapshotGeneratingCommitSha}
                  milestones={milestones}
                  openMilestoneEditor={openMilestoneEditor}
                  onViewCommit={viewCommitInHistory}
                  fetchRepoSnapshot={({ commitSha, buildTimeUtc }) =>
                    fetchRepoSnapshot({
                      commitSha,
                      buildTimeUtc,
                    })
                  }
                  beginDownloadRepoZip={beginDownloadRepoZip}
                />

                <CodeSnapshotCard
                  repoFiles={repoFiles}
                  sortedRepoFiles={sortedRepoFiles}
                  repoFilesUpdatedAt={repoFilesUpdatedAt}
                  repoFilesError={repoFilesError}
                  repoFilesLoading={repoFilesLoading}
                  repoFileCount={repoFileCount}
                  repoSnapshotVersion={repoSnapshotVersion}
                  repoSnapshotCommitSha={repoSnapshotCommitSha}
                  developerUnlocked={developerUnlocked}
                  isAdminUser={isAdminUser}
                  selectedRepoFile={selectedRepoFile}
                  setSelectedRepoFile={setSelectedRepoFile}
                  beginDownloadRepoZip={beginDownloadRepoZip}
                  milestones={milestones}
                  milestonesLoading={milestonesLoading}
                  milestonesError={milestonesError}
                  milestoneTargetCommitSha={milestoneTargetCommitSha}
                  milestoneDialogOpen={milestoneDialogOpen}
                  setMilestoneDialogOpen={handleMilestoneDialogOpenChange}
                  milestoneTitle={milestoneTitle}
                  setMilestoneTitle={setMilestoneTitle}
                  milestoneSaving={milestoneSaving}
                  createMilestone={createMilestone}
                  deleteSnapshotDialogOpen={deleteSnapshotDialogOpen}
                  setDeleteSnapshotDialogOpen={setDeleteSnapshotDialogOpen}
                  deleteSnapshotLoading={deleteSnapshotLoading}
                  deleteSnapshot={deleteSnapshot}
                  onJumpToPublishedBuilds={
                    () => {
                      goToSection('releases', 'published-builds');
                    }
                  }
                  onViewCommit={viewCommitInHistory}
                />
              </>
            )}
          </>
        )}

        <DeveloperPageDialogs
          reloadDialogOpen={reloadDialogOpen}
          setReloadDialogOpen={setReloadDialogOpen}
          handleReloadAllCommits={handleReloadAllCommits}
          downloadReasonDialogOpen={downloadReasonDialogOpen}
          setDownloadReasonDialogOpen={setDownloadReasonDialogOpen}
          downloadReason={downloadReason}
          setDownloadReason={setDownloadReason}
          confirmAdminDownloadRepoZip={confirmAdminDownloadRepoZip}
        />

        {developerUnlocked && (
          <>
            {(!isCompactView || mobileSection === 'time') && (
              <TimeExpenseTrackingCard
                totalHours={totalHours}
                totalExpenses={totalExpenses}
                timeDate={timeDate}
                setTimeDate={setTimeDate}
                timeHours={timeHours}
                setTimeHours={setTimeHours}
                timeHourlyRate={timeHourlyRate}
                setTimeHourlyRate={setTimeHourlyRate}
                handleAddTimeTicket={handleAddTimeEntry}
                timeDescription={timeDescription}
                setTimeDescription={setTimeDescription}
                timeTickets={timeTickets}
                gitOrderDescription={gitOrderDescription}
                setGitOrderDescription={setGitOrderDescription}
                timeMisc={timeMisc}
                setTimeMisc={setTimeMisc}
                timeTaxes={timeTaxes}
                setTimeTaxes={setTimeTaxes}
                createGitOrderFromSelectedCommits={createGitOrderFromSelectedCommits}
                gitOrders={gitOrders}
                commitsForSelectedTimeDate={commitsForSelectedTimeDate}
                timeEntryCommitShas={timeEntryCommitShas}
                setTimeEntryCommitShas={setTimeEntryCommitShas}
                timeEntryMarkCommitsComplete={timeEntryMarkCommitsComplete}
                setTimeEntryMarkCommitsComplete={setTimeEntryMarkCommitsComplete}
                openWorkorderCommitsDialog={openWorkorderCommitsDialog}
                deleteTimeEntry={deleteTimeEntry}
                linkTimeTicketToGitOrder={linkTimeTicketToGitOrder}
                expenseDate={expenseDate}
                setExpenseDate={setExpenseDate}
                expenseCategory={expenseCategory}
                setExpenseCategory={setExpenseCategory}
                expenseAmount={expenseAmount}
                setExpenseAmount={setExpenseAmount}
                expenseMisc={expenseMisc}
                setExpenseMisc={setExpenseMisc}
                expenseTaxes={expenseTaxes}
                setExpenseTaxes={setExpenseTaxes}
                handleAddExpense={handleAddExpense}
                expenseNotes={expenseNotes}
                setExpenseNotes={setExpenseNotes}
                expenses={expenses}
              />
            )}

            <WorkorderCommitsDialog
              open={workorderDialogOpen}
              onOpenChange={(open) => {
                setWorkorderDialogOpen(open);
                if (!open) {
                  setWorkorderDialogEntryId(null);
                  setWorkorderDialogCommitShas([]);
                  setWorkorderDialogRate('');
                  setWorkorderDialogHours('');
                  setWorkorderDialogHourlyRate('65.00');
                  setWorkorderDialogMisc('');
                  setWorkorderDialogTaxes('');
                }
              }}
              workorder={selectedWorkorder}
              commits={commitsForSelectedWorkorder}
              selectedCommitShas={workorderDialogCommitShas}
              setSelectedCommitShas={setWorkorderDialogCommitShas}
              rate={workorderDialogRate}
              setRate={setWorkorderDialogRate}
              hours={workorderDialogHours}
              setHours={setWorkorderDialogHours}
              hourlyRate={workorderDialogHourlyRate}
              setHourlyRate={setWorkorderDialogHourlyRate}
              misc={workorderDialogMisc}
              setMisc={setWorkorderDialogMisc}
              taxes={workorderDialogTaxes}
              setTaxes={setWorkorderDialogTaxes}
              onSave={saveWorkorderCommits}
              saving={workorderDialogSaving}
              onDeleteGitOrder={deleteTimeEntry}
            />

            <AssignCommitToWorkorderDialog
              open={assignCommitDialogOpen}
              onOpenChange={(open) => {
                setAssignCommitDialogOpen(open);
                if (!open) {
                  setAssignCommitDialogShas([]);
                  setAssignCommitDialogWorkorderId('');
                  setAssignCommitDialogNewWorkorderDescription('');
                }
              }}
              commits={commits}
              commitShas={assignCommitDialogShas}
              gitOrders={assignableWorkorders}
              selectedGitOrderId={assignCommitDialogWorkorderId}
              setSelectedGitOrderId={setAssignCommitDialogWorkorderId}
              newGitOrderDescription={assignCommitDialogNewWorkorderDescription}
              setNewGitOrderDescription={setAssignCommitDialogNewWorkorderDescription}
              onCreateGitOrder={createGitOrderFromCommits}
              creating={assignCommitDialogCreating}
              onAssign={assignCommitsToGitOrder}
              assigning={assignCommitDialogSaving}
            />

          </>
        )}

          </>
        )}

        <MobileDeveloperBottomNav
          enabled={isCompactView && !isPreviewEmbedded && availableMobileSections.length > 0}
          active={mobileSection}
          available={availableMobileSections}
          onChange={(id) => goToSection(id)}
        />
      </main>
    </div>
  );
}
